import { useState, useEffect, useCallback, useMemo } from "react";
import { Download, Mail, Ban, Scissors, Settings, Save, X, Loader2, Pencil, FilePlus, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { api, euro, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { GermanDatePicker } from "@/components/ui/date-picker";
import InvoiceEditor from "./InvoiceEditor";

const inputCls = "w-full bg-[#1A1A1A] border border-white/10 text-white p-2.5 text-sm focus:border-[#ff0033] outline-none";
const lbl = "text-neutral-500 text-[10px] uppercase tracking-widest font-mono-lab";

const DOC_LABEL = { invoice: "Rechnung", cancellation: "Storno", correction: "Korrektur" };
const STATUS_LABEL = { paid: "bezahlt", open: "offen", cancelled: "storniert", corrected: "korrigiert", refunded: "erstattet" };
const STATUS_CLS = { paid: "text-green-500", open: "text-amber-400", cancelled: "text-[#ff0033]", corrected: "text-amber-400", refunded: "text-blue-400" };
const MAIL_CLS = { sent: "text-green-500", failed: "text-[#ff0033]", logged: "text-amber-400", pending: "text-neutral-400" };

// Spalten-Definition inkl. Sortierschlüssel (null = nicht sortierbar)
const COLUMNS = [
  { label: "Nr.", key: "invoice_number", type: "num" },
  { label: "Typ", key: "doc_type", type: "str" },
  { label: "Kunde", key: "customer_name", type: "str" },
  { label: "Kurs", key: "course", type: "str" },
  { label: "Rg.-Datum", key: "issue_date", type: "date" },
  { label: "Leistung", key: "service_date", type: "date" },
  { label: "Brutto", key: "gross", type: "num" },
  { label: "Anbieter", key: "provider", type: "str" },
  { label: "Status", key: "status", type: "str" },
  { label: "E-Mail", key: "email_status", type: "str" },
  { label: "Aktionen", key: null },
];

const sortValue = (r, col) => {
  if (!col) return "";
  if (col.type === "num") return r[col.key] ?? 0;
  if (col.type === "date") return r[col.key] ? new Date(r[col.key]).getTime() : 0;
  return (r[col.key] ?? "").toString().toLowerCase();
};

const Invoices = ({ canManage }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [f, setF] = useState({ q: "", number: "", email: "", date_from: "", date_to: "", status: "" });
  const [sort, setSort] = useState({ key: null, dir: "asc" });

  const toggleSort = (col) => {
    if (!col.key) return;
    setSort((s) => s.key === col.key ? { key: col.key, dir: s.dir === "asc" ? "desc" : "asc" } : { key: col.key, dir: "asc" });
  };

  const load = useCallback(() => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    api.get("/admin/invoices", { params }).then((r) => setRows(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [f]);
  useEffect(() => { load(); }, [load]);

  const download = async (inv) => {
    try {
      const res = await api.get(`/admin/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `Rechnung-${inv.invoice_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download fehlgeschlagen"); }
  };
  const resend = async (inv) => {
    try { const r = await api.post(`/admin/invoices/${inv.id}/resend`); toast.success(r.data.delivery === "sent" ? "Rechnung per E-Mail gesendet" : `Versandstatus: ${r.data.delivery}`); load(); }
    catch { toast.error("Versand fehlgeschlagen"); }
  };
  const cancel = async (inv) => {
    const reason = window.prompt("Grund der Stornierung (Gutschrift wird erstellt):", "");
    if (reason === null) return;
    try { const r = await api.post(`/admin/invoices/${inv.id}/cancel`, { reason }); toast.success(`Stornorechnung ${r.data.invoice_number} erstellt`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  const correct = async (inv) => {
    const amountStr = window.prompt("Korrekturbetrag in € (brutto, z. B. Teil-Erstattung):", "");
    if (amountStr === null) return;
    const amount = Number(amountStr.replace(",", "."));
    if (!amount || amount <= 0) { toast.error("Ungültiger Betrag"); return; }
    const reason = window.prompt("Grund der Korrektur:", "") || "";
    try { const r = await api.post(`/admin/invoices/${inv.id}/correct`, { amount, reason }); toast.success(`Korrekturrechnung ${r.data.invoice_number} erstellt`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const [editInv, setEditInv] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const openEdit = async (inv) => {
    try { const r = await api.get(`/admin/invoices/${inv.id}`); setEditInv(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const totalGross = rows.filter((r) => r.doc_type === "invoice" && r.status === "paid").reduce((s, r) => s + (r.gross || 0), 0);

  const sortedRows = useMemo(() => {
    if (!sort.key) return rows;
    const col = COLUMNS.find((c) => c.key === sort.key);
    const arr = [...rows];
    arr.sort((a, b) => {
      const va = sortValue(a, col), vb = sortValue(b, col);
      let cmp;
      if (typeof va === "number" && typeof vb === "number") cmp = va - vb;
      else cmp = String(va).localeCompare(String(vb), "de", { numeric: true });
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [rows, sort]);

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-heading text-4xl uppercase tracking-tight text-white">Rechnungen</h1>
        {canManage && (
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => setCreateOpen(true)} data-testid="invoice-new-btn" className="flex items-center gap-2 bg-[#ff0033] text-white font-heading uppercase tracking-wider text-sm px-5 py-3 hover:bg-[#cc0029] transition-colors"><FilePlus size={16} /> Neue Rechnung</button>
            <button onClick={() => setShowSettings(!showSettings)} data-testid="invoice-settings-btn" className="flex items-center gap-2 border border-white/15 text-neutral-300 hover:border-[#ff0033] font-mono-lab text-xs uppercase tracking-widest px-4 py-3"><Settings size={15} /> Verkäufer-Stammdaten</button>
          </div>
        )}
      </div>

      {showSettings && <SellerSettings onClose={() => setShowSettings(false)} />}

      {/* Filter */}
      <div className="bg-[#0F0F0F] border border-white/10 p-4 mb-4 grid grid-cols-1 md:grid-cols-6 gap-2" data-testid="invoice-filters">
        <label className="block"><span className={lbl}>Suche (Kunde/Nr.)</span><input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} className={inputCls} data-testid="invoice-search" placeholder="Name, Firma…" /></label>
        <label className="block"><span className={lbl}>Rechnungsnr.</span><input value={f.number} onChange={(e) => setF({ ...f, number: e.target.value })} className={inputCls} placeholder="2026-…" /></label>
        <label className="block"><span className={lbl}>E-Mail</span><input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} /></label>
        <label className="block"><span className={lbl}>Von</span><GermanDatePicker value={f.date_from} onChange={(v) => setF({ ...f, date_from: v })} placeholder="Startdatum" className="p-2.5" /></label>
        <label className="block"><span className={lbl}>Bis</span><GermanDatePicker value={f.date_to} onChange={(v) => setF({ ...f, date_to: v })} placeholder="Enddatum" className="p-2.5" /></label>
        <label className="block"><span className={lbl}>Status</span>
          <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className={inputCls} data-testid="invoice-status-filter">
            <option value="">Alle</option>
            <option value="paid">bezahlt</option>
            <option value="open">offen</option>
            <option value="cancelled">storniert</option>
            <option value="corrected">korrigiert</option>
          </select></label>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
        <div className="bg-[#0F0F0F] border border-white/10 p-4"><p className={lbl}>Bezahlte Rechnungen (Umsatz brutto)</p><p className="font-heading text-2xl text-white mt-1">{euro(totalGross)}</p></div>
        <div className="bg-[#0F0F0F] border border-white/10 p-4"><p className={lbl}>Dokumente gesamt</p><p className="font-heading text-2xl text-white mt-1">{rows.length}</p></div>
      </div>

      <div className="overflow-x-auto bg-[#0F0F0F] border border-white/10">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-white/10 text-neutral-500 font-mono-lab text-[10px] uppercase tracking-widest">
            {COLUMNS.map((col) => {
              const active = sort.key === col.key;
              return (
                <th key={col.label} className="text-left p-3">
                  {col.key ? (
                    <button
                      type="button"
                      onClick={() => toggleSort(col)}
                      data-testid={`invoice-sort-${col.key}`}
                      className={`inline-flex items-center gap-1 uppercase tracking-widest hover:text-white transition-colors ${active ? "text-[#ff0033]" : ""}`}
                      title={`Nach ${col.label} sortieren`}
                    >
                      {col.label}
                      {active ? (sort.dir === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />) : <ChevronsUpDown size={12} className="opacity-40" />}
                    </button>
                  ) : col.label}
                </th>
              );
            })}
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan={11} className="p-6 text-center text-neutral-500"><Loader2 className="inline animate-spin" size={16} /></td></tr>}
            {!loading && sortedRows.length === 0 && <tr><td colSpan={11} className="p-6 text-center text-neutral-500">Keine Rechnungen gefunden.</td></tr>}
            {!loading && sortedRows.map((r) => (
              <tr key={r.id} className="border-b border-white/5 align-top" data-testid={`invoice-row-${r.invoice_number}`}>
                <td className="p-3 text-[#ff0033] font-mono-lab text-xs">{r.invoice_number}{r.original_invoice_number && <><br /><span className="text-neutral-500">→ {r.original_invoice_number}</span></>}</td>
                <td className="p-3"><span className="text-[10px] font-mono-lab uppercase text-neutral-300">{DOC_LABEL[r.doc_type] || r.doc_type}</span></td>
                <td className="p-3 text-neutral-200">{r.customer_name}<br /><span className="text-neutral-500 text-xs">{r.email}</span></td>
                <td className="p-3 text-neutral-300">{r.course}</td>
                <td className="p-3 text-neutral-400 text-xs">{fmtDate(r.issue_date)}</td>
                <td className="p-3 text-neutral-400 text-xs">{fmtDate(r.service_date)}</td>
                <td className={`p-3 font-mono-lab ${r.gross < 0 ? "text-blue-400" : "text-white"}`}>{euro(r.gross)}</td>
                <td className="p-3 text-neutral-300 text-xs">{r.provider}</td>
                <td className="p-3"><span className={`text-[10px] font-mono-lab uppercase ${STATUS_CLS[r.status] || "text-neutral-300"}`}>{STATUS_LABEL[r.status] || r.status}</span></td>
                <td className="p-3"><span className={`text-[10px] font-mono-lab uppercase ${MAIL_CLS[r.email_status] || "text-neutral-400"}`}>{r.email_status || "—"}</span></td>
                <td className="p-3">
                  <div className="flex items-center gap-1">
                    <button onClick={() => download(r)} title="PDF herunterladen" data-testid={`invoice-download-${r.invoice_number}`} className="p-1.5 text-neutral-400 hover:text-[#ff0033]"><Download size={15} /></button>
                    <button onClick={() => resend(r)} title="Per E-Mail senden" data-testid={`invoice-resend-${r.invoice_number}`} className="p-1.5 text-neutral-400 hover:text-[#ff0033]"><Mail size={15} /></button>
                    {canManage && (
                      <button onClick={() => openEdit(r)} title="Rechnung bearbeiten (PDF wird neu erstellt)" data-testid={`invoice-edit-${r.invoice_number}`} className="p-1.5 text-neutral-400 hover:text-[#ff0033]"><Pencil size={15} /></button>
                    )}
                    {canManage && r.doc_type === "invoice" && r.status !== "cancelled" && (
                      <>
                        <button onClick={() => cancel(r)} title="Stornieren (Gutschrift)" data-testid={`invoice-cancel-${r.invoice_number}`} className="p-1.5 text-neutral-400 hover:text-[#ff0033]"><Ban size={15} /></button>
                        <button onClick={() => correct(r)} title="Korrektur (Teil-Erstattung)" data-testid={`invoice-correct-${r.invoice_number}`} className="p-1.5 text-neutral-400 hover:text-[#ff0033]"><Scissors size={15} /></button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editInv && <InvoiceEditor inv={editInv} onClose={() => setEditInv(null)} onSaved={() => { setEditInv(null); load(); }} />}
      {createOpen && <InvoiceEditor inv={null} onClose={() => setCreateOpen(false)} onSaved={() => { setCreateOpen(false); load(); }} />}
    </div>
  );
};

const FIELDS = [
  ["name", "Firmenname (rechtlich)"], ["brand", "Zusatz-Marke"], ["street", "Straße & Hausnr."],
  ["postcode", "PLZ"], ["city", "Ort"], ["country", "Land"], ["tax_number", "Steuernummer"],
  ["vat_rate", "USt-Satz %"], ["account_holder", "Kontoinhaber"], ["bank_name", "Bank"], ["iban", "IBAN"], ["bic", "BIC"],
];

const SellerSettings = ({ onClose }) => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/invoice-settings").then((r) => setData(r.data)).catch(() => {}); }, []);
  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/invoice-settings", data); toast.success("Stammdaten gespeichert"); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); } finally { setSaving(false); }
  };
  if (!data) return null;
  return (
    <div className="bg-[#0F0F0F] border border-[#ff0033]/40 p-5 mb-4" data-testid="seller-settings">
      <div className="flex items-center justify-between mb-4">
        <p className="font-mono-lab text-xs tracking-widest text-[#ff0033] uppercase">Verkäufer-Stammdaten (erscheinen auf allen Rechnungen)</p>
        <button onClick={onClose} className="text-neutral-400 hover:text-white"><X size={18} /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {FIELDS.map(([k, label]) => (
          <label key={k} className="block"><span className={lbl}>{label}</span>
            <input value={data[k] ?? ""} onChange={(e) => setData({ ...data, [k]: e.target.value })} className={inputCls} data-testid={`seller-${k}`} /></label>
        ))}
      </div>
      <button onClick={save} disabled={saving} data-testid="save-seller-btn" className="mt-4 flex items-center gap-2 bg-[#ff0033] text-white font-heading uppercase tracking-wider px-6 py-3 disabled:opacity-50">{saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Speichern</button>
    </div>
  );
};

export default Invoices;
